import { ProductClientService, ProductSpotModel, PurchaseOptionType } from '@/types/productServerApi';
import { computed, ref, Ref } from 'vue';
import { UpSellProductItemRelation } from '@/b2c/shared/api/server-apis';
import utilitiesService from '@/shared/utilities.service';

const upsellSkuWithProductRelation: Ref<UpSellProductItemRelation[] | undefined> = ref(undefined);
const upsellItemsForBasket: Ref<ProductSpotModel[] | undefined> = ref([]);

export function useBasketUpsell() {
    const setUpsellSkuRelationsForBasket = (upsellItems: UpSellProductItemRelation[] | undefined) => {
        upsellSkuWithProductRelation.value = upsellItems;
    };

    const getUpsellItemsForLineItem = (lineItemId: number): ProductSpotModel[] | undefined => {
        const skuListForLineItem = upsellSkuListForEveryLineItem.value && upsellSkuListForEveryLineItem.value.find((lineItem) => lineItem.lineItemId === lineItemId);

        if (!skuListForLineItem || !upsellItemsForBasket.value) {
            return undefined;
        }

        return upsellItemsForBasket.value.filter((product) => skuListForLineItem.skuList.indexOf(product.sku) > -1 && productIsDirectlyPurchasable(product));
    };

    const fetchUpsellItemProductModel = async(): Promise<void> => {
        upsellItemsForBasket.value = await ProductClientService.getProductSpotsBySku(upsellItemSkuList.value, true).then(utilitiesService.unwrapModel);
    };

    const upsellItemSkuList = computed(() => {
        const upsellSkus = [] as string[];
        if (upsellSkuWithProductRelation.value) {
            upsellSkuWithProductRelation.value.forEach((upsellItem) => {
                if (upsellItem && upsellItem.sku) {
                    upsellSkus.push(upsellItem.sku);
                }
            });
        }
        return upsellSkus;
    });

    const upsellSkuListForEveryLineItem = computed(() => {
        const lineItemsWithUpsellItems: { lineItemId: number, skuList: (string | undefined)[] }[] = [];
        if (!upsellSkuWithProductRelation.value) {
            return;
        }
        upsellSkuWithProductRelation.value.forEach((upsellItem) => {
            upsellItem.items.forEach((upSellItem) => {
                const matchingLineItem = lineItemsWithUpsellItems.find((lineItem) => lineItem.lineItemId === upSellItem.itemId);
                if (matchingLineItem) {
                    matchingLineItem.skuList.push(upsellItem.sku);
                } else {
                    lineItemsWithUpsellItems.push({ lineItemId: upSellItem.itemId, skuList: [upsellItem.sku] });
                }
            });
        });

        return lineItemsWithUpsellItems;
    });

    const productIsDirectlyPurchasable = (product: ProductSpotModel): boolean => {
        if (product.purchaseOptions.length === 0) {
            return false;
        }

        const hasSinglePurchaseOption = product.purchaseOptions.length === 1;
        const onlyClickAndCollect = hasSinglePurchaseOption && product.purchaseOptions[0].type === PurchaseOptionType.ClickAndCollect;
        return !onlyClickAndCollect;
    };

    const getUpsellItemsFoundInBasket = (lineItemId: number, sku: string) => {
        const items = upsellSkuWithProductRelation.value?.find(x => x.sku === sku)?.items.find(y => y.itemId === lineItemId && y.isInBasket);

        return items;
    };

    return {
        setUpsellSkuRelationsForBasket,
        getUpsellItemsForLineItem,
        fetchUpsellItemProductModel,
        getUpsellItemsFoundInBasket,
    };
}
